import React from "react";
import { Card, CardTitle, Bar, Badge } from "../UI";
import styles from "../../_css/Simulation.module.css";
import Empty from "./Empty";
import NextButton from "./NextButton";
import { Search } from "lucide-react";
import MarketFlag from "./MarketFlag";

const NA = "Not available";
const NO_PROMPT = "No prompt was stored for this persona.";
// Drivers and barriers arrive sorted descending; show the strongest ones only.
const PURCHASE_LIST_LIMIT = 8;

function safeStr(v) {
  if (v == null) return NA;
  const s = typeof v === "string" ? v.trim() : String(v).trim();
  return s || NA;
}
function safeNum(v) {
  if (v == null || typeof v !== "number" || Number.isNaN(v)) return null;
  return v;
}
function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

function hasText(v) {
  return v != null && v !== NA && String(v).trim() !== "";
}

/** Detect if project is in the audience/persona format (audience_id, persona_profile, etc.) */
function isNewPersonaFormat(project) {
  return (
    project &&
    typeof project === "object" &&
    (project.persona_profile != null || project.audience_name != null)
  );
}

/** Map from new persona format to UI shape with safe parsing and NA fallbacks */
function fromNewPersonaFormat(project) {
  const pp = project.persona_profile || {};
  const core = pp.core_demographics || {};
  const location =
    core.location && typeof core.location === "object" ? core.location : {};

  const mediaArr = safeArr(pp.media_consumption);
  const mediaConsumption = [];
  for (let i = 0; i < 5; i++) {
    const item = mediaArr[i];
    const label = item ? safeStr(item.platform ?? item.label) : NA;
    const pct =
      item && (item.usage_percentage != null || item.reach_pct != null)
        ? safeNum(item.usage_percentage ?? item.reach_pct)
        : null;
    const idx = item && item.index != null ? item.index : null;
    mediaConsumption.push({ label, pct, idx });
  }

  const socialArr = safeArr(pp.social_platforms);
  const socialPlatforms = [];
  for (let i = 0; i < 4; i++) {
    const item = socialArr[i];
    const label = item ? safeStr(item.platform ?? item.label) : NA;
    const pct =
      item && (item.usage_percentage != null || item.active_posting_pct != null)
        ? safeNum(item.usage_percentage ?? item.active_posting_pct)
        : null;
    const idx = item && item.index != null ? item.index : null;
    socialPlatforms.push({ label, pct, idx });
  }

  const aiDigitalArr = safeArr(pp.ai_digital_behavior);
  const aiDigitalBehavior = aiDigitalArr.map((item) => ({
    title: safeStr(item?.label),
    desc: safeStr(item?.text),
    index: item?.index != null ? safeNum(item.index) : null,
  }));

  const trustArr = safeArr(pp.trust_sources);
  const trustSources = trustArr.slice(0, 4).map((t) => ({
    label: safeStr(t?.source),
    pct:
      t && (t.trust_percentage != null || t.percentage != null)
        ? safeNum(t.trust_percentage ?? t.percentage)
        : null,
    idx: t && t.index != null ? t.index : null,
  }));

  const driversArr = safeArr(pp.purchase_drivers);
  const purchaseDrivers = driversArr.map((d) => ({
    label: safeStr(d?.driver),
    pct: d && d.percentage != null ? safeNum(d.percentage) : null,
    desc: safeStr(d?.description),
    color: "#a855f7",
    // Personas generated before the index was kept for drivers and barriers have none, so those rows show no badge.
    idx: d && d.index != null ? d.index : null,
  }));

  const barriersArr = safeArr(pp.purchase_barriers);
  const purchaseBarriers = barriersArr.map((b) => ({
    label: safeStr(b?.barrier),
    pct: b && b.percentage != null ? safeNum(b.percentage) : null,
    desc: safeStr(b?.description),
    color: "#ef4444",
    idx: b && b.index != null ? b.index : null,
  }));

  const insightsArr = safeArr(pp.key_insights);
  const keyInsights = insightsArr.map((ins) => ({
    icon: ins && ins.icon != null ? String(ins.icon) : "📌",
    title: safeStr(ins?.segment_name ?? ins?.title),
    desc: safeStr(ins?.description),
  }));

  return {
    market: safeStr(location.primary_market),
    mediaConsumption,
    socialPlatforms,
    aiDigitalBehavior,
    trustSources,
    purchaseDrivers,
    purchaseBarriers,
    keyInsights,
  };
}

const EMPTY_PERSONA = {
  market: NA,
  mediaConsumption: [
    { label: "YouTube (last month)", pct: null },
    { label: "Automotive websites/apps", pct: null },
    { label: "Streaming preroll", pct: null },
    { label: "Search engines", pct: null },
    { label: "Mobile/tablet app ads", pct: null },
  ],
  socialPlatforms: [
    { label: "TikTok", pct: null },
    { label: "LinkedIn", pct: null },
    { label: "Snapchat", pct: null },
    { label: "Facebook Messenger", pct: null },
  ],
  aiDigitalBehavior: [],
  trustSources: [
    { label: "Reliability (primary trust)", pct: null, idx: null },
    { label: "Authenticity preference", pct: null, idx: null },
    { label: "Trust online reviews", pct: null, idx: null },
    { label: "Use consumer reviews when researching", pct: null, idx: null },
  ],
  purchaseDrivers: [],
  purchaseBarriers: [],
  keyInsights: [],
};
const pct = (v) =>
  v != null && typeof v === "number" ? Math.round(v * 100) : null;

function fromPersona(project) {
  if (!project || typeof project !== "object") return null;
  if (isNewPersonaFormat(project)) return fromNewPersonaFormat(project);
  const media = project.media_consumption || {};
  const trust = project.trust_barriers || {};
  const strat = project.strategic_implications || {};
  const evProfile = project.ev_buyer_profile || {};

  const mediaLabels = [
    "YouTube (last month)",
    "Automotive websites/apps",
    "Streaming preroll",
    "Search engines",
    "Mobile/tablet app ads",
  ];
  const yt = media.youtube_behavior || {};
  const disc = media.brand_discovery_channels || {};
  const mediaSources = [
    () =>
      yt.used_youtube_last_month != null
        ? pct(yt.used_youtube_last_month)
        : null,
    () =>
      disc.automotive_websites_apps?.reach_pct != null
        ? pct(disc.automotive_websites_apps.reach_pct)
        : null,
    () =>
      disc.streaming_preroll_ads?.reach_pct != null
        ? pct(disc.streaming_preroll_ads.reach_pct)
        : null,
    () =>
      disc.search_engines?.reach_pct != null
        ? pct(disc.search_engines.reach_pct)
        : null,
    () =>
      disc.mobile_tablet_app_ads?.reach_pct != null
        ? pct(disc.mobile_tablet_app_ads.reach_pct)
        : null,
  ];
  const mediaConsumption = mediaLabels.map((label, i) => ({
    label,
    pct: mediaSources[i](),
  }));

  const socialKeys = ["tiktok", "linkedin", "snapchat", "facebook_messenger"];
  const socialLabels = ["TikTok", "LinkedIn", "Snapchat", "Facebook Messenger"];
  const social = media.social_media_usage || {};
  const socialPlatforms = socialKeys.map((key, i) => {
    const o = social[key];
    const val =
      o && o.active_posting_pct != null ? pct(o.active_posting_pct) : null;
    return { label: socialLabels[i], pct: val };
  });

  const tech = evProfile.technology_affinity || {};
  const evMedia = media.ev_intender_media_habits || {};
  const aiDigitalSlots = [
    () =>
      tech.early_adopters?.percentage != null
        ? {
            pct: pct(tech.early_adopters.percentage),
            idx: tech.early_adopters.index_vs_ev_intenders,
            label: "Early adopters",
          }
        : null,
    () =>
      tech.tech_confident?.percentage != null
        ? {
            pct: pct(tech.tech_confident.percentage),
            idx: tech.tech_confident.index_vs_ev_intenders,
            label: "Tech confident",
          }
        : null,
    () =>
      tech.tech_enthusiasts?.percentage != null
        ? {
            pct: pct(tech.tech_enthusiasts.percentage),
            idx: tech.tech_enthusiasts.index_vs_ev_intenders,
            label: "Tech enthusiasts",
          }
        : null,
    () =>
      evMedia.prefer_technology_podcasts?.percentage != null
        ? {
            pct: pct(evMedia.prefer_technology_podcasts.percentage),
            idx: evMedia.prefer_technology_podcasts.index_vs_average,
            label: "Prefer tech podcasts",
          }
        : null,
  ];
  const aiDigitalLabels = [
    "Early adopters",
    "Tech confident",
    "Tech enthusiasts",
    "Prefer tech podcasts",
  ];
  const aiDigital = aiDigitalSlots.map((fn, i) => {
    const r = fn();
    return r || { pct: null, idx: null, label: aiDigitalLabels[i] || NA };
  });
  const aiDigitalBehavior = aiDigital
    .filter((a) => a.pct != null || (a.label && a.label !== NA))
    .map((a) => ({
      title: safeStr(a.label),
      desc:
        a.pct != null
          ? `${a.pct}%${a.idx != null ? ` · Index ${a.idx}` : ""}`
          : NA,
      index: a.idx != null ? safeNum(a.idx) : null,
    }));

  const primary = trust.primary_trust_factors || {};
  const review = trust.review_trust_gap || {};
  const brandE = trust.brand_enthusiasm || {};
  const trustItems = [
    {
      label: "Reliability (primary trust)",
      pct:
        primary.reliability_concern?.percentage != null
          ? pct(primary.reliability_concern.percentage)
          : null,
      idx: null,
    },
    {
      label: "Authenticity preference",
      pct:
        primary.authenticity_preference?.percentage != null
          ? pct(primary.authenticity_preference.percentage)
          : null,
      idx: null,
    },
    {
      label: "Trust online reviews",
      pct:
        review.trust_online_reviews?.percentage != null
          ? pct(review.trust_online_reviews.percentage)
          : null,
      idx: null,
    },
    {
      label: "Use consumer reviews when researching",
      pct:
        review.use_consumer_reviews_when_researching?.percentage != null
          ? pct(review.use_consumer_reviews_when_researching.percentage)
          : null,
      idx:
        review.use_consumer_reviews_when_researching?.index_vs_average ?? null,
    },
    {
      label: "Brand enthusiasts",
      pct:
        brandE.brand_enthusiasts?.percentage != null
          ? pct(brandE.brand_enthusiasts.percentage)
          : null,
      idx: null,
    },
  ];
  const trustSources = trustItems.slice(0, 4);

  const price = trust.price_sensitivity || {};
  const purchaseItems = [
    {
      label: "Look for best deals",
      pct:
        price.look_for_best_deals?.percentage != null
          ? pct(price.look_for_best_deals.percentage)
          : null,
      desc: price.look_for_best_deals?.notes || "",
    },
    {
      label: "Willing to pay for premium",
      pct:
        price.willing_to_pay_for_premium?.percentage != null
          ? pct(price.willing_to_pay_for_premium.percentage)
          : null,
      desc: price.willing_to_pay_for_premium?.notes || "",
    },
    {
      label: "Describe as price conscious",
      pct:
        price.describe_as_price_conscious?.percentage != null
          ? pct(price.describe_as_price_conscious.percentage)
          : null,
      desc: price.describe_as_price_conscious?.notes || "",
    },
    {
      label: "Reliability concern",
      pct:
        primary.reliability_concern?.percentage != null
          ? pct(primary.reliability_concern.percentage)
          : null,
      desc: primary.reliability_concern?.notes || "",
    },
  ];
  const purchaseDrivers = purchaseItems.map((p) => ({
    ...p,
    pct: p.pct,
    desc: p.desc || NA,
  }));

  const keyInsights = [];
  const recs = strat.key_recommendations || [];
  recs.forEach((r) => {
    keyInsights.push({
      icon: "📌",
      title: r?.title || NA,
      desc: r?.description || NA,
    });
  });
  if (keyInsights.length === 0) {
    const matrix = strat.priority_matrix || [];
    matrix.forEach((m) => {
      keyInsights.push({
        icon: "📌",
        title: m?.implication || NA,
        desc: m?.key_data_point || NA,
      });
    });
  }

  return {
    market: project.market || NA,
    mediaConsumption,
    socialPlatforms,
    aiDigitalBehavior,
    trustSources,
    purchaseDrivers,
    purchaseBarriers: [],
    keyInsights: keyInsights.filter(
      (ins) => hasText(ins?.title) || hasText(ins?.desc),
    ),
  };
}

function PersonaTab({
  project = null,
  audiencePrompt = null,
  projectId = null,
  onPage = null,
  recentSimulationStatus = null,
  onGoToQueries = null,
}) {
  const derived = fromPersona(project);
  const hasProject = !!project && typeof project === "object";

  if (!hasProject) {
    if (recentSimulationStatus == -1 || recentSimulationStatus == null) {
      return (
        <Empty message="Failed simulation. Please create a new simulation again" />
      );
    }
  }

  if (!hasProject) {
    return (
      <Empty message="Persona generation in progress..." type="progress" />
    );
  }
  const market = hasProject && derived?.market ? derived.market : NA;
  const promptText = typeof audiencePrompt === "string" ? audiencePrompt.trim() : "";

  const mediaConsumption =
    hasProject && derived?.mediaConsumption
      ? derived.mediaConsumption
      : EMPTY_PERSONA.mediaConsumption;

  const socialPlatforms =
    hasProject && derived?.socialPlatforms
      ? derived.socialPlatforms
      : EMPTY_PERSONA.socialPlatforms;

  const aiDigitalBehavior =
    hasProject && derived?.aiDigitalBehavior
      ? derived.aiDigitalBehavior
      : EMPTY_PERSONA.aiDigitalBehavior;

  const trustSources =
    hasProject && derived?.trustSources
      ? derived.trustSources
      : EMPTY_PERSONA.trustSources;

  const purchaseDrivers =
    hasProject && derived?.purchaseDrivers
      ? derived.purchaseDrivers
      : EMPTY_PERSONA.purchaseDrivers;

  const purchaseBarriers =
    hasProject && derived?.purchaseBarriers
      ? derived.purchaseBarriers
      : EMPTY_PERSONA.purchaseBarriers;

  const keyInsights =
    hasProject && derived?.keyInsights
      ? derived.keyInsights
      : EMPTY_PERSONA.keyInsights;

  const mediaRows = mediaConsumption.filter((m) => m.pct != null);
  const socialRows = socialPlatforms.filter((m) => m.pct != null);
  const trustRows = trustSources.filter((t) => t.pct != null);
  const purchaseRows = purchaseDrivers
    .filter((p) => hasText(p?.label) && (p.pct != null || hasText(p?.desc)))
    .slice(0, PURCHASE_LIST_LIMIT);
  const barrierRows = purchaseBarriers
    .filter((p) => hasText(p?.label) && (p.pct != null || hasText(p?.desc)))
    .slice(0, PURCHASE_LIST_LIMIT);
  const insightRows = keyInsights.filter(
    (ins) => hasText(ins?.title) || hasText(ins?.desc),
  );

  const renderPurchaseRows = (rows) => (
    <div className={styles.personaBarListSpaced}>
      {rows.length === 0 ? (
        <div className={styles.personaSectionEmpty}>{NA}</div>
      ) : (
        rows.map((p, i) => {
          const descIsNa = !hasText(p.desc);
          return (
            <div key={i} className={styles.personaBarItem}>
              <div className={styles.personaBarRowCenter}>
                <span className={styles.personaBarLabel}>
                  {hasText(p.label) ? p.label : NA}
                </span>
                <div className={styles.personaBarBadgeWrap}>
                  <span className={styles.personaBarValue}>
                    {p.pct != null ? `${p.pct}%` : NA}
                  </span>
                  {p.idx != null && (
                    <Badge color="#22c55e">Idx {p.idx}</Badge>
                  )}
                </div>
              </div>
              {p.pct != null ? (
                <Bar pct={p.pct} color={p.color || "#a855f7"} showLabel={false} />
              ) : null}
              {!descIsNa && (
                <div className={styles.personaBarDesc}>{p.desc}</div>
              )}
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div className={styles.personaPage}>
      <Card className={styles.personaProfileCard}>
        <div className={styles.personaProfileRow}>
          <div>
            <h2 className={styles.personaProfileTitle}>Audience Profile</h2>
            <div className={styles.personaProfileMarket}>
              <MarketFlag market={market} />
              <div>
                <div className={styles.personaMarketLabel}>Market</div>
                <div className={styles.personaMarketValue}>{market}</div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <CardTitle>Audience Prompt</CardTitle>
        <div className={styles.personaPromptBox}>
          {promptText ? (
            <div className={styles.personaPromptText}>{promptText}</div>
          ) : (
            <div className={styles.personaPromptEmpty}>{NO_PROMPT}</div>
          )}
        </div>
      </Card>

      <div className={styles.personaTwoColGrid}>
        <Card>
          <CardTitle>Media Consumption</CardTitle>
          <div className={styles.personaBarList}>
            {mediaRows.length === 0 ? (
              <div className={styles.personaSectionEmpty}>{NA}</div>
            ) : (
              mediaRows.map((m, i) => (
                <div key={i} className={styles.personaBarItem}>
                  <div className={styles.personaBarRowCenter}>
                    <span className={styles.personaBarLabel}>{m.label}</span>
                    <div className={styles.personaBarBadgeWrap}>
                      <span className={styles.personaBarValue}>
                        {m.pct != null ? `${m.pct}%` : NA}
                      </span>
                      {m.idx != null && (
                        <Badge color="#22c55e">Idx {m.idx}</Badge>
                      )}
                    </div>
                  </div>
                  <Bar pct={m.pct ?? 0} showLabel={false} />
                </div>
              ))
            )}
          </div>
        </Card>
        <Card>
          <CardTitle>Social Platforms</CardTitle>
          <div className={styles.personaBarList}>
            {socialRows.length === 0 ? (
              <div className={styles.personaSectionEmpty}>{NA}</div>
            ) : (
              socialRows.map((m, i) => (
                <div key={i} className={styles.personaBarItem}>
                  <div className={styles.personaBarRowCenter}>
                    <span className={styles.personaBarLabel}>{m.label}</span>
                    <div className={styles.personaBarBadgeWrap}>
                      <span className={styles.personaBarValue}>
                        {m.pct != null ? `${m.pct}%` : NA}
                      </span>
                      {m.idx != null && (
                        <Badge color="#22c55e">Idx {m.idx}</Badge>
                      )}
                    </div>
                  </div>
                  <Bar pct={m.pct ?? 0} color="#22c55e" showLabel={false} />
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      <div className={styles.personaTwoColGrid}>
        <Card>
          <CardTitle>Trust Sources</CardTitle>
          <div className={styles.personaBarListSpaced}>
            {trustRows.length === 0 ? (
              <div className={styles.personaSectionEmpty}>{NA}</div>
            ) : (
              trustRows.map((t, i) => (
                <div key={i} className={styles.personaBarItem}>
                  <div className={styles.personaBarRowCenter}>
                    <span className={styles.personaBarLabel}>{t.label}</span>
                    <div className={styles.personaBarBadgeWrap}>
                      <span className={styles.personaBarValue}>
                        {t.pct != null ? `${t.pct}%` : NA}
                      </span>
                      {t.idx != null && (
                        <Badge color="#22c55e">Idx {t.idx}</Badge>
                      )}
                    </div>
                  </div>
                  <Bar pct={t.pct ?? 0} color="#f59e0b" showLabel={false} />
                </div>
              ))
            )}
          </div>
        </Card>
        <Card>
          <CardTitle>Purchase Drivers</CardTitle>
          {renderPurchaseRows(purchaseRows)}
          <div className={styles.personaSectionGap}>
            <CardTitle>Purchase Barriers</CardTitle>
          </div>
          {renderPurchaseRows(barrierRows)}
        </Card>
      </div>

      <Card className={styles.personaInsightsCard}>
        <CardTitle>Key Persona Insights</CardTitle>
        <div className={styles.personaInsightsGrid}>
          {insightRows.length === 0 ? (
            <div className={styles.personaSectionEmpty}>{NA}</div>
          ) : (
            insightRows.map((ins, i) => (
              <div key={i} className={styles.personaInsightItem}>
                <div className={styles.personaInsightIcon}>{ins.icon}</div>
                <div className={styles.personaInsightTitle}>{ins.title}</div>
                <div className={styles.personaInsightDesc}>{ins.desc}</div>
              </div>
            ))
          )}
        </div>
      </Card>

      <Card>
        <CardTitle sub="How this audience interacts with AI">
          AI & Digital Behavior
        </CardTitle>
        <div className={styles.personaInsightsGrid}>
          {aiDigitalBehavior.length === 0 ? (
            <div className={styles.personaSectionEmpty}>{NA}</div>
          ) : (
            aiDigitalBehavior.map((item, i) => (
              <div key={i} className={styles.personaInsightItem}>
                <div className={styles.personaBarRowCenter}>
                  <div className={styles.personaInsightTitle}>{item.title}</div>
                  {item.index != null && (
                    <span className={styles.personaInsightIndex}>
                      Index {item.index}
                    </span>
                  )}
                </div>
                <div className={styles.personaInsightDesc}>{item.desc}</div>
              </div>
            ))
          )}
        </div>
      </Card>

      {typeof onGoToQueries === "function" && (
        <div className={styles.personaActions}>
          <NextButton
            label="Continue to Query Workshop"
            onClick={onGoToQueries}
            icon={<Search size={16} />}
          />
        </div>
      )}
    </div>
  );
}

export default PersonaTab;
